import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Helmet } from 'react-helmet';
import { Clock, ArrowRight, ChefHat, Flame, Dumbbell, Users } from 'lucide-react';
import { recipesData } from '@/data/recipesData';

const categories = ['All', ...new Set(recipesData.map((r) => r.category))];

const RecipesPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? recipesData
    : recipesData.filter((r) => r.category === activeCategory);

  return (
    <>
      <Helmet>
        <title>High-Protein Recipes - Healthy Fitness Meals | NutraFuel Supplements</title>
        <meta name="description" content="Easy, high-protein recipes for gym-goers and athletes. Protein smoothies, pancakes, overnight oats, and healthy snacks with macros and step-by-step instructions." />
        <meta name="keywords" content="high protein recipes, protein smoothie, protein pancakes, gym diet recipes, healthy fitness meals, bodybuilding recipes india" />
        <link rel="canonical" href="https://nutrafuel.in/recipes" />
        <meta property="og:title" content="High-Protein Recipes | NutraFuel Supplements" />
        <meta property="og:description" content="Easy, high-protein recipes for gym-goers and athletes." />
        <meta property="og:url" content="https://nutrafuel.in/recipes" />
      </Helmet>

      <Layout>
        {/* Hero */}
        <section className="relative pt-32 md:pt-40 pb-16 bg-gradient-to-b from-muted/50 to-background">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-4">
                <ChefHat className="w-5 h-5 text-primary" />
                <span className="text-primary font-semibold text-sm uppercase tracking-wider">Recipes &amp; Nutrition</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground leading-tight mb-6" data-testid="recipes-heading">
                High-Protein <span className="text-primary">Recipes</span>
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Simple, delicious, macro-friendly recipes to fuel your training. Every recipe includes full nutritional breakdown and step-by-step instructions.
              </p>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-6 bg-background border-b border-border">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap gap-2 justify-center" data-testid="recipe-category-filter">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    activeCategory === cat
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                  }`}
                  data-testid={`recipe-filter-${cat.toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Recipe */}
        <section className="py-8 bg-background">
          <div className="container mx-auto px-4">
            <Link to={`/recipes/${filtered[0].slug}`} data-testid="featured-recipe">
              <Card className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300">
                <div className="grid md:grid-cols-2">
                  <div className="aspect-video md:aspect-auto overflow-hidden bg-muted">
                    <img
                      src={filtered[0].image}
                      alt={filtered[0].title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <CardContent className="p-6 md:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-3 mb-4">
                      <Badge className="bg-primary/10 text-primary border-0">{filtered[0].category}</Badge>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {filtered[0].prepTime}
                      </span>
                      <Badge variant="secondary" className="text-xs">{filtered[0].difficulty}</Badge>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                      {filtered[0].title}
                    </h2>
                    <p className="text-muted-foreground mb-5 line-clamp-3">{filtered[0].excerpt}</p>
                    {/* Macro badges */}
                    <div className="flex flex-wrap gap-3 mb-6">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-orange-100 text-orange-700 px-2.5 py-1 rounded-full">
                        <Flame className="w-3 h-3" /> {filtered[0].calories} cal
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full">
                        <Dumbbell className="w-3 h-3" /> {filtered[0].protein} protein
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
                        <Users className="w-3 h-3" /> {filtered[0].servings} serving{filtered[0].servings > 1 ? 's' : ''}
                      </span>
                    </div>
                    <span className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
                      View Full Recipe
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </CardContent>
                </div>
              </Card>
            </Link>
          </div>
        </section>

        {/* All Recipes Grid */}
        <section className="py-12 bg-background" data-testid="recipes-grid">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl font-display font-bold text-foreground mb-8">
              {activeCategory === 'All' ? 'All Recipes' : activeCategory} ({filtered.length})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((recipe) => (
                <Link key={recipe.id} to={`/recipes/${recipe.slug}`} data-testid={`recipe-card-${recipe.id}`}>
                  <Card className="group overflow-hidden border-0 shadow-soft hover:shadow-elevated transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                    <div className="aspect-video overflow-hidden bg-muted relative">
                      <img
                        src={recipe.image}
                        alt={recipe.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 right-3">
                        <Badge className="bg-background/90 text-foreground text-xs backdrop-blur-sm">{recipe.difficulty}</Badge>
                      </div>
                    </div>
                    <CardContent className="p-5 flex flex-col flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge variant="secondary" className="text-xs">{recipe.category}</Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {recipe.prepTime}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {recipe.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4 flex-1 line-clamp-2">{recipe.excerpt}</p>
                      {/* Macros row */}
                      <div className="flex items-center gap-2 mt-auto pt-3 border-t border-border">
                        <span className="text-xs font-medium text-orange-600">{recipe.calories} cal</span>
                        <span className="text-border">|</span>
                        <span className="text-xs font-medium text-blue-600">{recipe.protein} protein</span>
                        <span className="text-border">|</span>
                        <span className="text-xs font-medium text-muted-foreground">{recipe.carbs} carbs</span>
                        <span className="text-border">|</span>
                        <span className="text-xs font-medium text-muted-foreground">{recipe.fat} fat</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Content */}
        <section className="py-12 bg-muted/20 border-t border-border">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-display font-bold text-foreground mb-4">
                Fuel Your Gains with the Right Nutrition
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Great results in the gym start in the kitchen. Our high-protein recipes are designed by athletes for athletes &mdash; simple ingredients, real macros, and meals that actually taste good. Pair them with NutraFuel supplements for maximum results.
              </p>
            </div>
          </div>
        </section>
      </Layout>
    </>
  );
};

export default RecipesPage;
